/**
 * One date range, shared by every report and the services behind them.
 *
 * Nick, 1 Sept: "all the reports should be able to have a range filter." Each
 * report had invented its own period control — a dropdown of whole months here, a
 * quarter-to-date chip there, nothing at all on two of them — so the same question
 * could not be asked the same way twice. The rules live here, in lib, because the
 * services need them as much as the screens do.
 *
 * Empty means unbounded. An empty range is all of time, which is exactly what
 * every report showed before it had a filter, so the control changes no number
 * until someone picks a window.
 */

export interface DateRange {
  from: string
  to: string
}

export const ALL_TIME: DateRange = { from: '', to: '' }

export const isoDay = (d: Date) => d.toISOString().slice(0, 10)

/**
 * `to` is INCLUSIVE. Someone asking for the 30th means the 30th, not up to the
 * morning of it — the commonest way a date filter quietly loses a day.
 *
 * A row with no date at all only survives an unbounded range: once a window is
 * chosen, something undated cannot honestly be said to fall inside it.
 */
export function inRange(iso: string | null | undefined, range: DateRange): boolean {
  if (!range.from && !range.to) return true
  if (!iso) return false
  const day = String(iso).slice(0, 10)
  if (range.from && day < range.from) return false
  if (range.to && day > range.to) return false
  return true
}

export function rangeLabel(range: DateRange): string {
  const pretty = (s: string) =>
    new Date(s + 'T00:00:00Z').toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  if (!range.from && !range.to) return 'All Time'
  if (range.from && !range.to) return `From ${pretty(range.from)}`
  if (!range.from && range.to) return `Up to ${pretty(range.to)}`
  return `${pretty(range.from)} – ${pretty(range.to)}`
}

/** The Australian financial year containing `d` — 1 July to 30 June. */
function fyBounds(d: Date): DateRange {
  const y = d.getUTCFullYear()
  const startYear = d.getUTCMonth() >= 6 ? y : y - 1
  return { from: `${startYear}-07-01`, to: `${startYear + 1}-06-30` }
}

export function presetRange(key: string): DateRange {
  const now = new Date()
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  const first = (yy: number, mm: number) => isoDay(new Date(Date.UTC(yy, mm, 1)))
  const last = (yy: number, mm: number) => isoDay(new Date(Date.UTC(yy, mm + 1, 0)))
  switch (key) {
    case 'this_month':
      return { from: first(y, m), to: last(y, m) }
    case 'last_month':
      return { from: first(y, m - 1), to: last(y, m - 1) }
    case 'this_quarter': {
      const q = Math.floor(m / 3) * 3
      return { from: first(y, q), to: last(y, q + 2) }
    }
    case 'this_fy':
      return fyBounds(now)
    case 'last_fy':
      return fyBounds(new Date(Date.UTC(y - 1, m, 1)))
    default:
      return ALL_TIME
  }
}
